import PropTypes from 'prop-types';
import Card from 'components/Card';
import IconHeading from 'components/IconHeading';
import { useQuery } from '@apollo/client';
import { EXTRA_EMPLOYER_DATA } from 'lib/queries';
import { useTranslation } from 'next-i18next';

const EmployerList = ({
  Icon,
  heading,
  HeadingTag,
  employers,
  compact,
  cta,
  extraClasses,
  loading,
}) => {
  const { t } = useTranslation();
  const employerSlugs = employers.map(({ node: { slug } }) => slug);
  const { data } = useQuery(EXTRA_EMPLOYER_DATA, {
    variables: {
      slugs: employerSlugs,
    },
  });
  const extraData = data?.companyBranches?.edges?.map(
    ({
      node: {
        slug,
        onlineVacancies: { totalCount: onlineVacancies },
      },
    }) => ({
      slug,
      onlineVacancies,
    })
  );

  return (
    <div
      className={`c-job-posting-list  ${
        loading ? 'c-job-posting-list--loading' : ''
      } ${extraClasses || ''}`
        .replace(/\s+/g, ' ')
        .trim()}
    >
      {!!Icon && !!heading && (
        <IconHeading Icon={Icon} heading={heading} HeadingTag={HeadingTag} />
      )}
      <ul
        className="o-list-clean c-job-posting-list__list"
        data-cy="employer-list"
      >
        {employers.map(
          ({
            node: {
              id,
              name,
              slug,
              address: { location },
              companyName,
              logoUrl,
            },
          }) => {
            const currentItem = extraData?.find?.(item => item.slug === slug);
            const { onlineVacancies } = currentItem || {};
            let context = '';
            if (typeof onlineVacancies === 'undefined') {
              context = 'loading';
            } else if (onlineVacancies === 0) {
              context = 'none';
            } else {
              context = 'loaded';
            }

            const note = t('employersOverview.vacancies', {
              context,
              count: onlineVacancies,
            });

            return (
              <li key={id} className="u-margin-bottom-small--not-last-child">
                <Card
                  title={name}
                  ctaTag="a"
                  href={`/vacatures/${slug}`}
                  company={companyName}
                  location={location}
                  cta={!compact && cta ? cta : ''}
                  test="employer-item"
                  dataId={id}
                  note={note}
                  image={logoUrl}
                  imageWidth={150}
                  imageHeight={75}
                  showImagePlaceholder
                  extraClasses={
                    onlineVacancies && onlineVacancies === 0
                      ? 'c-card--note-disabled'
                      : null
                  }
                />
              </li>
            );
          }
        )}
      </ul>
    </div>
  );
};

EmployerList.propTypes = {
  Icon: PropTypes.func,
  heading: PropTypes.string,
  HeadingTag: PropTypes.string,
  employers: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        companyName: PropTypes.string.isRequired,
        address: PropTypes.shape({ location: PropTypes.string.isRequired }),
        logo: PropTypes.shape({
          contentUrl: PropTypes.string,
          image: PropTypes.bool,
        }),
      }),
    })
  ).isRequired,
  compact: PropTypes.bool,
  cta: PropTypes.string,
  extraClasses: PropTypes.string,
  loading: PropTypes.bool,
};

EmployerList.defaultProps = {
  compact: false,
  loading: false,
};

export default EmployerList;
